import { cloneElement, isValidElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { useEsCrafterFlag } from '../../common/EsFlags';
import { esCrafterHas, esCrafterList, esCrafterPagePath, useEsCrafterLocalized } from '../../common/EsCrafter';
import { useEsLocale } from '../../common/EsLocale';
import { useAuth } from '../parent/EsAuth';
import EsErrorPage from '../util/EsErrorPage';
import { esBlockFor } from './blocks';
import { EsLegalOwed, EsLegalPolicies } from '../legal/EsLegal';

/*
 * A page from Crafter, drawn by escapade's own block renderers (./blocks). The route
 * /view/page/:slug serves any page an admin adds in Studio; EsHome and EsLegal hand their
 * slugs here while switchEsCrafter is on.
 *
 * Two layouts, chosen by the first block: a hero opens a marketing page (the home page's
 * stacked full-width sections), anything else opens an article (EsLegal's centered column).
 *
 * `fallback` is what draws while the page loads and when neither Crafter nor the snapshot
 * has it: the page as esfront writes it, for home and legal, so the switch on can only
 * ever show today's page or Crafter's. A slug nothing knows is the 404.
 *
 * THE PAGE SAYS WHEN IT IS SETTLED. The answer replaces the fallback's whole tree, so the
 * fallback's state goes with it: a contact box typed into before Crafter (or, behind a dead
 * proxy, the snapshot) answered came back blank, and AQA's Home.feature:159 sent an empty
 * message on 2026-10-21 when the lane's dev server took three seconds to say 502. The root
 * carries data-settled -- false on the fallback while the answer is pending, true on the
 * answer, absent on a fallback that is final and with the switch off -- so a step (and a
 * reader) can tell the page that stays from the one about to be replaced. The swap itself
 * still loses what was typed; a visitor who types inside that window is the open finding,
 * not fixed here.
 */

const XB_SCRIPT = '/studio/static-assets/scripts/craftercms-xb.umd.js';

let xbLoading = null;

function startExperienceBuilder( pPath ) {
    if ( !xbLoading ) {
        xbLoading = new Promise( ( pResolve, pReject ) => {
            const aScript = document.createElement( 'script' );
            aScript.src = XB_SCRIPT;
            aScript.onload = pResolve;
            aScript.onerror = pReject;
            document.head.appendChild( aScript );
        } );
    }

    return xbLoading
        .then( () => window.craftercms?.xb?.initExperienceBuilder( { path: pPath } ) )
        .catch( () => null );
}

export function esStudioLink( pPath ) {
    const aStudio = process.env.ES_CRAFTER_STUDIO;
    if ( !aStudio ) return '';
    return aStudio.replace( /\/$/, '' ) + '/studio/preview#/?page=' + encodeURIComponent( pPath ) + '&site=escapade';
}

function EsCrafterEditLink( { item } ) {
    const auth = useAuth();
    const aHref = esStudioLink( item.path );

    if ( !auth?.admin || item.authoring || !aHref ) return null;

    return (
        <a href={ aHref } target="_blank" rel="noreferrer" data-testid="es-page-edit-link"
           className="fixed bottom-8 left-8 z-[999] rounded-md bg-panel px-3 py-2 text-sm text-chrome-ink shadow-md hover:bg-panel-hover">
            <i className="pi pi-pencil mr-2" aria-hidden="true" />Edit
        </a>
    );
}

export function EsCrafterBlocks( { slug, item } ) {
    const aEntries = esCrafterList( item.root.sections_o );
    // The legal page's policy links are code's, not Studio's: with no "policies" marker in the
    // page they follow its first block, the heading, where EsLegal draws them.
    const aPoliciesAfter = slug === 'legal' && !aEntries.some( ( pEntry ) => pEntry?.component?.marker_s === 'policies' ) ? 0 : -1;
    return aEntries.flatMap( ( pEntry, pIndex ) => {
        const aBlock = pEntry?.component;
        const Block = aBlock ? esBlockFor( aBlock[ 'content-type' ] ) : null;
        const aDrawn = Block ? [ <Block key={ aBlock.objectId ?? pIndex } slug={ slug } block={ aBlock } item={ item } /> ] : [];
        return pIndex === aPoliciesAfter ? [ ...aDrawn, <EsLegalPolicies key="policies" /> ] : aDrawn;
    } );
}

/* What a page owes a reader that Crafter's editable copy cannot be trusted to carry, by slug,
   drawn after its blocks: the notices makers' packs owe the legal hub (EsLegalOwed); its
   policy links are placed by EsCrafterBlocks. Keyed here rather than on the route so /legal and /view/page/legal agree.
   The escapade-written page draws its own. */
const OWED = { legal: EsLegalOwed };

export default function EsCrafterPage( { slug: pSlug, fallback = null, own = false } ) {
    const { slug: aRouteSlug } = useParams();
    const aSlug = pSlug ?? aRouteSlug;
    const aOn = useEsCrafterFlag();
    const aLocale = useEsLocale();
    const { item, loading } = useEsCrafterLocalized( ( pLocale ) => esCrafterPagePath( pLocale, aSlug ), aLocale, aOn && Boolean( aSlug ), own );

    useEffect( () => {
        if ( item?.authoring ) startExperienceBuilder( item.path );
    }, [ item ] );

    if ( !aOn ) return fallback ?? <EsErrorPage />;
    const Owed = OWED[ aSlug ];
    const owed = Owed ? <Owed /> : null;
    if ( loading ) return isValidElement( fallback ) ? cloneElement( fallback, { settled: false } ) : fallback;
    if ( !item ) return fallback ?? <EsErrorPage />;

    const aFirst = esCrafterList( item.root.sections_o )[ 0 ]?.component?.[ 'content-type' ];

    if ( aFirst === '/component/hero' ) {
        return (
            <div data-testid={ `es-${ aSlug }-page` } data-crafter-source={ item.source } data-settled="true">
                <EsCrafterBlocks slug={ aSlug } item={ item } />
                { owed }
                <EsCrafterEditLink item={ item } />
            </div>
        );
    }

    return (
        <section data-testid={ `es-${ aSlug }-page` } data-crafter-source={ item.source } data-settled="true" className="relative z-10 pb-4 pt-nav-room">
            <div className="container">
                <div className="mx-auto px-4 xl:w-[800px] max-sm:break-words">
                    <EsCrafterBlocks slug={ aSlug } item={ item } />
                    { owed }
                </div>
            </div>
            <EsCrafterEditLink item={ item } />
        </section>
    );
}

/**
 * A page escapade writes itself, or Crafter's page of the same slug while switchEsCrafter is
 * on and Crafter has the page in the reader's language. Otherwise the children untouched,
 * which is what makes off render exactly today. Crafter having the language is not enough:
 * fr has a home and no legal, and Crafter's en legal stood in for a French reader, in
 * English and without the page's English-governs notice and policy links (locale sweep,
 * 2026-10-19), which the children carry.
 */
export function EsCrafterSwitch( { slug, children } ) {
    const aLocale = useEsLocale();
    const aOn = useEsCrafterFlag() && esCrafterHas( aLocale );
    return aOn ? <EsCrafterPage slug={ slug } fallback={ children } own /> : children;
}
